import { getLocaleText } from "shared/boot/i18n";
import { isAdminMode } from "shared/constants";
import { getSource, type Medium } from "shared/helpers/media";
import DialogService from "shared/modals/DialogService";
import {
  type OrganisationFeature,
  type OrganisationFeatures,
  useOrganisationFeaturesStore,
} from "shared/stores/organisationFeatures";
import { useUserStore } from "shared/stores/user";
import { type FeatureKey } from "shared/types/features";
import { type Mention } from "shared/types/mentions";

import activities from "./activities";

const BETA_FEATURES: FeatureKey[] = [
  "has_sentiment_rating_mode",
  "has_social_platform",
  "has_social_engage",
  "has_outreach",
];

function getFeatures(): OrganisationFeatures {
  return useOrganisationFeaturesStore().organisationFeatures;
}

function isAdminUser(): boolean {
  return useUserStore().isAdminUser || isAdminMode;
}

export default {
  alwaysWarn(mention: Mention): boolean {
    const source = getSource(mention.type);

    const alwaysShowPopup = source.viewPopupShown === "always";

    const hasWarningFeature =
      source.viewWarningFeatureFlag && this.has(source.viewWarningFeatureFlag);

    const noCredits =
      source.featureAllowanceFlag &&
      !this.hasCredits(source.featureAllowanceFlag);

    const reachedWarning = this.reachedWarning(source);

    return Boolean(
      alwaysShowPopup && hasWarningFeature && (noCredits || reachedWarning)
    );
  },

  featureUnavailable(featureName?: string): void {
    if (!featureName) return;

    DialogService.alert({
      message: getLocaleText(
        "services.features.feature_unavailable_description"
      ),
      title: getLocaleText("services.features.feature_unavailable_title", {
        featureName,
      }),
    });
  },

  getContractAllowance(featureId: FeatureKey): number {
    return this.getFeature(featureId).contract_allowance;
  },

  getCost(feature: FeatureKey): number {
    return this.getFeature(feature).cost;
  },

  getCredits(feature: FeatureKey): number {
    return this.getFeature(feature).credits;
  },

  getFeature(featureId: FeatureKey): Required<OrganisationFeature> {
    return {
      contract_allowance: 0,
      cost: 0,
      credits: 0,
      total_used_credits: 0,
      used_credits: 0,
      ...getFeatures()[featureId],
    };
  },

  getFeatureUnitKeypath(feature: FeatureKey): string {
    return feature === "audio_transcript" ? "services.features.minute" : "";
  },

  getTotalUsedCredits(featureId: FeatureKey): number {
    return this.getFeature(featureId).total_used_credits;
  },

  getUsagePercentage(featureId: FeatureKey): number {
    const feature = this.getFeature(featureId);

    if (!feature.contract_allowance) return 0;
    if (feature.total_used_credits > feature.contract_allowance) return 100;

    const percentage =
      (feature.total_used_credits / feature.contract_allowance) * 100;

    if (percentage < 1) {
      return Number(percentage.toFixed(2));
    }

    return Math.round(percentage);
  },

  getUsedCredits(feature: FeatureKey): number {
    return this.getFeature(feature).used_credits;
  },

  has(feature?: FeatureKey): boolean {
    if (!feature) return true;

    let features = null;

    if (isAdminMode) {
      features = Object.keys(
        useOrganisationFeaturesStore().organisationFeatures
      );

      if (!features.length) return true;
    } else {
      features = useUserStore().organisation?.features || [];
    }

    const featureSet = [
      ...(features || []),
      ...(useUserStore().betaEnabled ? BETA_FEATURES : []),
    ];

    return featureSet.includes(feature);
  },

  hasAll(features: FeatureKey[] = []): boolean {
    return features.every((feature) => this.has(feature));
  },

  hasAny(features: FeatureKey[] = []): boolean {
    return features.some((feature) => this.has(feature));
  },

  hasCredits(feature?: FeatureKey): boolean {
    if (!feature) return true;

    const credits = this.getCredits(feature);

    // if credits is null, it's unlimited
    if (credits === null || credits > 0) {
      return true;
    }

    return false;
  },

  // True only for orgs with a positive contract allowance that have used up
  // their instant-edit credit (allowance fully consumed).
  // Pay-per-use orgs (contract_allowance === 0) always return false
  // so they are never blocked and fall through to the cost-confirmation flow.
  hasFiniteContractAllowanceDepleted(featureId: FeatureKey): boolean {
    return (
      this.getContractAllowance(featureId) >= 0 &&
      this.reachedContractAllowance(featureId)
    );
  },

  noMoreMonthlyCredits(featureId: FeatureKey): boolean {
    const feature = this.getFeature(featureId);

    return feature.credits !== null && feature.used_credits >= feature.credits;
  },

  reachedContractAllowance(featureId: FeatureKey): boolean {
    const feature = this.getFeature(featureId);

    return (
      feature.credits !== null &&
      feature.total_used_credits >= feature.contract_allowance
    );
  },

  reachedWarning(medium: Medium): boolean {
    const warningFeature = medium.viewWarningFeatureFlag
      ? this.getFeature(medium.viewWarningFeatureFlag)
      : null;

    const allowanceFeature = medium.featureAllowanceFlag
      ? this.getFeature(medium.featureAllowanceFlag)
      : null;

    if (warningFeature && allowanceFeature) {
      return (
        allowanceFeature.credits !== null &&
        allowanceFeature.used_credits > warningFeature.credits
      );
    }

    return false;
  },

  async requiresActivityRecord(mention: Mention): Promise<boolean> {
    const source = getSource(mention.type);

    if (source.viewFeatureFlag && !this.has(source.viewFeatureFlag)) {
      this.featureUnavailable(source.featureName || "");

      return false;
    }

    if (isAdminUser()) {
      return false;
    }

    let alreadyRecordedActivity = false;

    try {
      alreadyRecordedActivity =
        (await activities.getActivitiesCount(mention)) > 0;

      if (!alreadyRecordedActivity && source.featureAllowanceFlag) {
        const refreshCreditsPromises = [
          useOrganisationFeaturesStore().refreshUsedCredits({
            feature: source.featureAllowanceFlag,
            organisationId: useUserStore().organisation.id,
          }),
        ];

        if (source.viewWarningFeatureFlag) {
          refreshCreditsPromises.push(
            useOrganisationFeaturesStore().refreshUsedCredits({
              feature: source.viewWarningFeatureFlag,
              handleErrors: false,
              organisationId: useUserStore().organisation.id,
            })
          );
        }

        await Promise.all(refreshCreditsPromises);
      }
    } catch {
      return false;
    }

    if (!alreadyRecordedActivity && this.alwaysWarn(mention)) {
      return true;
    }

    return false;
  },
};
